import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Spinner } from '../../../core/ui';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../../../core/context/TranslationContext';
import { acceptInvitation, previewInvitation } from '../api/family';
import { useFamily } from '../context/FamilyContext';
import styles from './AcceptInvite.module.css';

export default function AcceptInvite() {
  const { token } = useParams();
  const { t } = useTranslation();
  const { refresh } = useFamily();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await previewInvitation(token);
        setPreview(data);
      } catch (err) {
        setError(err.response?.data?.message || t('family.invite.invalid'));
      } finally {
        setLoading(false);
      }
    })();
  }, [token, t]);

  const handleAccept = async () => {
    try {
      setAccepting(true);
      setError(null);
      await acceptInvitation(token);
      await refresh();
      setSuccess(true);
      setTimeout(() => navigate('/family'), 1500);
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'ALREADY_IN_FAMILY') setError(t('family.invite.alreadyMember'));
      else if (code === 'INVITATION_INVALID') setError(t('family.invite.invalid'));
      else setError(err.response?.data?.message || t('error'));
    } finally {
      setAccepting(false);
    }
  };

  if (loading) return <Spinner label={t('loading')} className={styles.spinner} />;

  return (
    <div className={styles.container}>
      <Card>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">Joined! Redirecting…</Alert>}
          {preview && !success && (
            <>
              <Card.Title>{preview.familyName}</Card.Title>
              <p className={styles.mutedText}>Owner: {preview.ownerUsername}</p>
              {preview.expiresAt && (
                <p className={styles.smallMuted}>Expires: {new Date(preview.expiresAt).toLocaleString()}</p>
              )}
              {preview.alreadyMember && (
                <Alert variant="info">{t('family.invite.alreadyMember')}</Alert>
              )}
              {preview.alreadyInOtherFamily && (
                <Alert variant="warning">{t('family.invite.inAnotherFamily')}</Alert>
              )}
              {preview.valid && !preview.alreadyMember && !preview.alreadyInOtherFamily && (
                <Button variant="primary" onClick={handleAccept} disabled={accepting}>
                  {accepting ? t('loading') : 'Join Family'}
                </Button>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
